import type { ViewIds } from '@ardoq/api-types';
import { colors, s16, s24, spacing } from '@ardoq/design-tokens';
import styled from 'styled-components';
import { ViewOptionsRow } from './atoms';
import { Icon, IconName, HelpIcon } from '@ardoq/icons';
import { text2 } from '@ardoq/typography';
import { DropdownDivider } from '@ardoq/dropdown-2026';
import { UnstyledLink } from '@ardoq/link';
import { t, type WithI18nLocale } from '@ardoq/i18n';
import { getPaginationCoreLabels } from 'locales/getPaginationLabels';
import { getViewIdUserFriendlyName } from 'views/metaInfoTabs';
import {
  NumberVisualProperties,
  Pagination,
  PaginationController,
  RenderArgs,
} from '@ardoq/pagination';
import { Attributes, ReactNode, useCallback } from 'react';
import { viewInfo } from './viewInfo';
import { BetaBadge } from '@ardoq/status-ui';
import { openEphemeralModalNotification } from 'ephemeralNotification/modalNotification/openEphemeralModalNotification';
import { trackEventV2 } from 'tracking/tracking';
import type { ViewPickerViewId } from 'views/environmentals/getViewpointModeSupportedViews';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  user-select: none;
`;
const Divider = styled(DropdownDivider)`
  align-self: stretch;
  margin: 0 ${s16};
`;
const PickerContainer = styled.div`
  display: flex;
  width: 100%;
  padding: ${spacing.s4} 0;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;
const CardsContainer = styled.div`
  display: flex;
  padding: 0 ${s16} ${spacing.s8} ${s16};
  align-items: center;
  align-content: center;
  gap: ${s24} ${s16};
  flex-shrink: 0;
  align-self: stretch;
`;
const InfoRow = styled(ViewOptionsRow)`
  padding: 0 ${s16} ${spacing.s8};
  color: ${colors.grey500};
`;

const InfoBox = styled.div`
  display: flex;
  min-height: 24px;
  padding: 0;
  align-items: center;
  gap: ${spacing.s4};
  flex: 1 0 0;
`;

const HelpLink = styled(UnstyledLink)`
  display: flex;
`;

const InfoLabel = styled.label`
  flex: 1 0 0;
  margin: 0;
  color: ${colors.textSubtle};
  ${text2}
`;

const VISIBLE_VIEW_COUNT = 3;

type CardInfo = Pick<Attributes, 'key'> & ViewPickerCardProperties;
type ViewPickerCardProperties = {
  isSelected: boolean;
  title: string;
  onClick: VoidFunction;
  media: ReactNode;
};

type CardContainerProperties = { $isActive: boolean };
const CardContainer = styled.div<CardContainerProperties>`
  display: flex;
  height: 106px;
  width: 130px;
  min-width: 118.182px;
  max-width: 141.818px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 9.455px;
  border: ${({ $isActive }) =>
    $isActive
      ? `1.18182px solid ${colors.brand600}`
      : `0.591px solid ${colors.grey300}`};
  &:hover {
    box-shadow:
      0 0 1.182px 0 rgba(0, 0, 0, 0.2),
      0 1.182px 5.909px 0 rgba(0, 0, 0, 0.14);
  }
  background: ${colors.white950};
  cursor: pointer;
`;

const ImageContainer = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  img {
    height: 100%;
    object-fit: contain;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  min-height: fit-content;
  padding: ${spacing.s8};
  flex-direction: column;
  align-items: flex-start;
  gap: 18.909px;
  align-self: stretch;
`;

const TitleContent = styled.div`
  display: flex;
  padding: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 9.455px;
  align-self: stretch;
`;

const TitleContentInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const TextContainer = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  color: ${colors.grey950};
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 15px;
`;
const ViewPickerCard = ({
  media,
  title,
  onClick,
  isSelected,
}: ViewPickerCardProperties) => (
  <CardContainer
    onClick={onClick}
    $isActive={isSelected}
    role="radio"
    aria-label={title}
    aria-checked={isSelected}
    tabIndex={isSelected ? 0 : -1}
  >
    {media}
    <TitleContainer>
      <TitleContent>
        <TitleContentInner>
          <TextContainer title={title}>{title}</TextContainer>
        </TitleContentInner>
      </TitleContent>
    </TitleContainer>
  </CardContainer>
);
const PaginationContainer = styled.div`
  display: flex;
  height: 32px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
`;

const BulletElementContainer = styled.div`
  display: flex;
  width: 16px;
  height: 32px;
  padding: 2px;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;
type BulletElementProperties = { $isActive: boolean };
const BulletElement = styled.div<BulletElementProperties>`
  display: flex;
  width: 8px;
  height: 8px;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
  border-radius: 20px;
  background: ${({ $isActive }) =>
    $isActive ? colors.grey900 : colors.grey200};
  cursor: pointer;
`;
const Bullet = ({ isActive, onClick }: NumberVisualProperties) => (
  <BulletElementContainer>
    <BulletElement $isActive={isActive} onClick={onClick} />
  </BulletElementContainer>
);

const BETA_ICON_MARGIN = 4;

type ViewInfoRowProps = { mainViewId: ViewIds } & WithI18nLocale;

const ViewInfoRow = ({ mainViewId, i18nLocale }: ViewInfoRowProps) => {
  const {
    knowledgeBaseLink,
    description,
    isBeta,
    badgeKnowledgeBaseLink,
    shareFeedbackLink,
    onboardingModal,
    takeTour,
    badgeOptionsOrder,
    badgeLabelOverrides,
  } = viewInfo[mainViewId] ?? {};

  return (
    <InfoRow>
      <InfoBox>
        {knowledgeBaseLink && (
          <HelpLink
            href={knowledgeBaseLink}
            target="_blank"
            aria-label={t(
              i18nLocale,
              'appLayout:knowledgeBase',
              'Knowledge base'
            )}
          >
            <HelpIcon color={colors.icon} />
          </HelpLink>
        )}
        <InfoLabel>
          {description
            ? t(i18nLocale, description.i18nKey, description.defaultValue)
            : t(i18nLocale, 'appLayout:learnAboutView', 'Learn about the view')}
        </InfoLabel>
        {isBeta && (
          <BetaBadge
            shareFeedbackUrl={shareFeedbackLink}
            knowledgeBaseUrl={badgeKnowledgeBaseLink}
            i18nLocale={i18nLocale}
            openOnboardingModal={
              onboardingModal
                ? () => {
                    if (onboardingModal.reopenTrackingEvent) {
                      trackEventV2(onboardingModal.reopenTrackingEvent);
                    }
                    openEphemeralModalNotification(
                      onboardingModal.notificationKey,
                      i18nLocale
                    );
                  }
                : undefined
            }
            onTakeTour={
              takeTour
                ? () => {
                    if (takeTour.trackingEvent) {
                      trackEventV2(takeTour.trackingEvent);
                    }
                    window.open(takeTour.url, '_blank');
                  }
                : undefined
            }
            optionsOrder={badgeOptionsOrder}
            labelOverrides={badgeLabelOverrides}
          />
        )}
      </InfoBox>
    </InfoRow>
  );
};

export type ViewPickerProps = {
  mainViewId: ViewIds;
  viewPickerViewIds: ViewPickerViewId[];
  onViewSelected: (renderedViewId: ViewIds) => void;
} & WithI18nLocale;

const ViewPicker = ({
  mainViewId,
  viewPickerViewIds,
  onViewSelected,
  i18nLocale,
}: ViewPickerProps) => {
  const CardsPage = useCallback(
    ({
      currentPageDataSource,
      currentPageNumber,
      perPage,
      totalResults,
      onPageSelect,
    }: RenderArgs<CardInfo>) => (
      <PickerContainer>
        <CardsContainer
          role="radiogroup"
          aria-label={t(
            i18nLocale,
            'appLayout:viewSelectionAriaLabel',
            'View selection'
          )}
        >
          {currentPageDataSource.map(({ key, ...cardProps }) => (
            <ViewPickerCard key={key} {...cardProps} />
          ))}
        </CardsContainer>
        <ViewInfoRow mainViewId={mainViewId} i18nLocale={i18nLocale} />
        <PaginationContainer>
          <Pagination
            currentPageNumber={currentPageNumber}
            perPage={perPage}
            totalResults={totalResults}
            onPageSelect={onPageSelect}
            NumberVisual={Bullet}
            {...getPaginationCoreLabels(i18nLocale)}
          />
        </PaginationContainer>
      </PickerContainer>
    ),
    [i18nLocale, mainViewId]
  );

  const cardInfo: CardInfo[] = viewPickerViewIds.map(
    ({ viewId, renderedViewId }) => {
      return {
        key: viewId,
        title: getViewIdUserFriendlyName(i18nLocale, viewId),
        media: (
          <ImageContainer>
            <img width="130" height="73" src={viewInfo[viewId]?.image} />
            {viewInfo[renderedViewId]?.isBeta && (
              <Icon
                iconName={IconName.BETA}
                color={colors.blue600}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  margin: BETA_ICON_MARGIN,
                }}
              />
            )}
          </ImageContainer>
        ),
        onClick: () => onViewSelected(renderedViewId),
        isSelected: mainViewId === renderedViewId,
      };
    }
  );

  const startPageNumber =
    1 +
    Math.floor(
      viewPickerViewIds
        .map(({ renderedViewId }) => renderedViewId)
        .indexOf(mainViewId) / VISIBLE_VIEW_COUNT
    );

  return (
    <Container>
      <PaginationController
        startPageNumber={startPageNumber}
        perPage={VISIBLE_VIEW_COUNT}
        dataSource={cardInfo}
        render={CardsPage}
      />
      <Divider />
    </Container>
  );
};

export { ViewPicker };
